/** The workshop's panels. Each takes the workshop document (or one scenario of
 *  it) and draws it; none of them fetch. */

import { FaCar, FaCloud, FaHeartbeat, FaUsers, FaInfoCircle } from 'react-icons/fa';
import { fmtChange, tone } from './format';
import { figure, affordability, reasonFor, sameAs, routeCostId, corridorsIn, CORRIDOR_ROWS } from './figures';
import { groupName, groupsOf, corridorName, ratioText, noteText } from '../../utils/corridors';
import ServiceLevelToggle from '../ServiceLevelToggle';

const CARD_ICONS = { car: FaCar, air: FaCloud, health: FaHeartbeat, equity: FaUsers };

export const ILLUSTRATIVE = 'Illustrative, not modelled';

// The SF demo shows figures without "Model" / "Illustrative" tags (2026-10-28). The
// components stay so a later demo can bring the tags back here in one place.
const SHOW_FIGURE_TAGS = false;

/** Goes with every figure that is not a model result, as visible as the figure. */
function IllustrativeMark({ compact = false }) {
  if (!SHOW_FIGURE_TAGS) return null;
  return <span className={`ws-ill-mark${compact ? ' ws-ill-mark-compact' : ''}`}>{ILLUSTRATIVE}</span>;
}

/** Marks a figure from the traffic simulation. */
export function ModelTag() {
  if (!SHOW_FIGURE_TAGS) return null;
  return <span className="ws-tag ws-tag-model" title="From the traffic simulation">Model</span>;
}

/** The figures' kind for a tooltip ("(model)"), or nothing while the tags are off. */
const kindNote = (kind) => (!SHOW_FIGURE_TAGS ? '' : kind === 'model' ? ' (model)' : ` (${ILLUSTRATIVE.toLowerCase()})`);

/** The service level of a scenario with variants (`level`, the one shown), shared with the
 *  pricing panel; nothing for any other scenario. */
export function ScenarioLevelToggle({ scenario, onServiceLevel, className = '' }) {
  if (!scenario.variants || !scenario.level || !onServiceLevel) return null;
  return (
    <ServiceLevelToggle className={className} levels={scenario.variants.map((v) => v.service_level)}
      value={scenario.level.service_level} onChange={onServiceLevel} />
  );
}

/** Scenario list with radio rows (the picker in the first mockup). */
export function ScenarioList({ scenarios, selectedId, onSelect }) {
  return (
    <div className="ws-scenario-list" role="radiogroup">
      {scenarios.map((s) => (
        <button
          key={s.id}
          role="radio"
          aria-checked={s.id === selectedId}
          className={`ws-scenario-row ${s.id === selectedId ? 'selected' : ''}`}
          onClick={() => onSelect(s.id)}
        >
          <span className="ws-radio" />
          <span className="ws-scenario-code" style={{ color: s.color }}>{s.code}</span>
          <span className="ws-scenario-text">
            <span className="ws-scenario-name">{s.name}</span>
            <span className="ws-scenario-summary">{s.summary}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** One figure in a cell: a model result with its Model tag and the counts behind it, an
 *  illustrative value with its mark, or the baseline's reference. */
function Figure({ f, metric }) {
  if (f.kind === 'empty') return <>—</>;
  if (f.kind === 'baseline') {
    return <>{fmtChange(f.value, metric.unit)}{f.detail && <span className="ws-cell-detail">{f.detail}</span>}</>;
  }
  if (f.kind === 'illustrative') return <>{fmtChange(f.value, metric.unit)}<IllustrativeMark compact /></>;
  return (
    <>
      {f.value != null && f.value !== 0 && <span className="ws-arrow">{f.value < 0 ? '▼' : '▲'}</span>}
      {fmtChange(f.value, metric.unit)} <ModelTag />
      {f.detail && <span className="ws-cell-detail">{f.detail}</span>}
    </>
  );
}

/** A model figure that equals another scenario's because of how the scenario is built. */
function SameMark({ of, why }) {
  return <span className="ws-cell-detail ws-same" title={`By construction: ${why}`}>Same as {of.code}</span>;
}

const toneOf = (f, metric) => (f.kind === 'illustrative' ? 'ill' : f.kind === 'model' ? tone(f.value, metric.better) : 'neutral');

/** The four headline cards along the bottom. Each has a mini bar chart of the
 *  same metric across the pricing scenarios, the selected one highlighted. A scenario
 *  with no figures in the hours shown gets the reason instead. */
export function ImpactCards({ workshop, scenario, metrics, period }) {
  const reason = reasonFor(workshop, scenario.id, period);
  if (reason) {
    return (
      <div className="ws-cards-reason" role="status">
        <FaInfoCircle /> {scenario.code} · {scenario.name}, {period.label}: {reason}
      </div>
    );
  }
  const pricing = workshop.scenarios.filter((s) => s.id !== 'baseline');
  const isBaseline = scenario.id === 'baseline';
  return (
    <>
    <div className="ws-cards-note">
      Headline figures are for San Francisco, where the charge applies; the Analytics maps cover the whole Bay Area.
    </div>
    <div className="ws-cards">
      {workshop.cards.map((c) => {
        const Icon = CARD_ICONS[c.icon] || FaInfoCircle;
        const m = metrics[c.metric];
        const f = figure(workshop, period, scenario, c.metric);
        const values = pricing.map((s) => Math.abs(figure(workshop, period, s, c.metric).value || 0));
        const max = Math.max(1, ...values);
        const ill = f.kind === 'illustrative';
        return (
          <div className={`ws-card${ill && SHOW_FIGURE_TAGS ? ' ws-card-ill' : ''}`} key={c.title}>
            <div className={`ws-card-icon ws-icon-${c.icon}`}><Icon size={18} /></div>
            <div className="ws-card-body">
              <div className="ws-card-title">{c.title}</div>
              {isBaseline ? (
                <>
                  <div className="ws-card-value neutral">--</div>
                  <div className="ws-card-caption">Select a scenario to view results.</div>
                </>
              ) : (
                <>
                  <div className={`ws-card-value ${toneOf(f, m)}`}>{fmtChange(f.value, m.unit)}</div>
                  {ill && <IllustrativeMark />}
                  <div className="ws-card-caption">{c.caption}{f.kind === 'model' && <> <ModelTag /></>}</div>
                </>
              )}
            </div>
            <div className="ws-card-bars" aria-hidden="true">
              {pricing.map((s, i) => {
                const g = figure(workshop, period, s, c.metric);
                return (
                  <span
                    key={s.id}
                    title={`${s.code}: ${fmtChange(g.value, m.unit)}${kindNote(g.kind)}`}
                    className={s.id === scenario.id ? 'on' : ''}
                    style={{ height: `${20 + 80 * (values[i] / max)}%` }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
    </>
  );
}

/** Metric × scenario grid (the comparison mockup). A scenario with no figures in the
 *  hours shown gets its reason once, down its whole column; one with service levels has
 *  their toggle in its head. `thumb(scenario)` renders the map at the head of its
 *  column, above the name. */
export function ComparisonGrid({ workshop, metrics, period, selectedId, onSelect, onServiceLevel, thumb }) {
  const corridors = corridorsIn(workshop, period);
  const extra = corridors ? CORRIDOR_ROWS.filter((r) => corridors[r.group]) : [];
  const rows = [...workshop.comparison_rows, ...extra.map((r) => r.id)];
  const metricOf = (id) => metrics[id] || extra.find((r) => r.id === id);
  const reasons = Object.fromEntries(workshop.scenarios.map((s) => [s.id, reasonFor(workshop, s.id, period)]));
  const sames = workshop.scenarios
    .map((s) => ({ s, of: reasons[s.id] ? null : rows.map((id) => sameAs(workshop, period, s, id)).find(Boolean) }))
    .filter((x) => x.of);
  const cell = (s, id) => {
    const m = metricOf(id);
    const sel = s.id === selectedId ? 'selected' : '';
    const same = sameAs(workshop, period, s, id);
    const mark = same && <SameMark of={same} why={s.same_as.why} />;
    if (id === 'affordability') {
      const a = affordability(workshop, period, s);
      if (a.kind === 'model') {
        return (
          <td key={s.id} className={`ws-classes ${sel}`}>
            <span className="ws-cell-detail">Average charge per entering vehicle <ModelTag /></span>
            {a.classes.map((c) => (
              <span className="ws-class-row" key={c.id}><span>{c.name}</span><span>${c.usd.toFixed(2)}</span></span>
            ))}
            {mark}
          </td>
        );
      }
      if (a.kind === 'baseline' || a.kind === 'model-none') {
        return <td key={s.id} className={`neutral ${sel}`}>{a.kind === 'baseline' ? a.text : '—'}{a.kind === 'model-none' && <span className="ws-cell-detail">{a.text}</span>}</td>;
      }
      return <td key={s.id} className={`${toneOf(a, m)} ${sel}`}><Figure f={a} metric={m} /></td>;
    }
    const f = figure(workshop, period, s, id);
    return <td key={s.id} className={`${toneOf(f, m)} ${sel}`} title={f.title || undefined}><Figure f={f} metric={m} />{mark}</td>;
  };
  // The scenarios share the width left by the metric column, so a map is as wide as the
  // figures under it; a column still grows for a figure that needs more.
  const colWidth = `${80 / workshop.scenarios.length}%`;
  return (
    <div className="ws-grid-wrap">
      <table className="ws-grid">
        <thead>
          {thumb && (
            <tr className="ws-grid-thumbs">
              <th style={{ width: '20%' }} />
              {workshop.scenarios.map((s) => (
                <th key={s.id} className={s.id === selectedId ? 'selected' : ''} style={{ width: colWidth }}>{thumb(s)}</th>
              ))}
            </tr>
          )}
          <tr>
            <th>Metric (vs. baseline){period && <span className="ws-cell-detail">{period.label}</span>}</th>
            {workshop.scenarios.map((s) => (
              <th key={s.id} className={s.id === selectedId ? 'selected' : ''} onClick={() => onSelect(s.id)}>
                <span className="ws-code" style={{ background: s.color }}>{s.code}</span>
                <span>{s.name}</span>
                <ScenarioLevelToggle scenario={s} onServiceLevel={onServiceLevel} className="ws-grid-level" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((id, r) => (
            <tr key={id}>
              <th>{metricOf(id).label}</th>
              {workshop.scenarios.map((s) => {
                if (!reasons[s.id]) return cell(s, id);
                return r === 0
                  ? <td key={s.id} rowSpan={rows.length} className={`ws-reason-cell ${s.id === selectedId ? 'selected' : ''}`}>{reasons[s.id]}</td>
                  : null;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="ws-muted ws-grid-note">
        {SHOW_FIGURE_TAGS && <>Figures tagged Model are the traffic model&apos;s, for the hours shown; the others are illustrative workshop values.</>}
        {sames.map(({ s, of }) => (
          <span key={s.id}>{' '}Scenario {s.code} equals scenario {of.code} where marked: {s.same_as.why}.</span>
        ))}
        {extra.length > 0 && ' The band rows count vehicles, each once, on the streets of the bands scenario 4 protects and on the residential streets beside them.'}
      </div>
    </div>
  );
}

const int = (n) => Math.round(n).toLocaleString('en-US');
const num = (n) => (Math.round(n) < 0 ? `−${int(-n)}` : int(n));
const signedInt = (d) => (Math.round(d) === 0 ? '±0' : `${d < 0 ? '−' : '+'}${int(Math.abs(d))}`);
const hourAt = (h) => {
  const n = ((h % 24) + 24) % 24;
  if (n === 0) return '12 am';
  if (n === 12) return '12 pm';
  return n > 12 ? `${n - 12} pm` : `${n} am`;
};
const hourRange = (h) => `${hourAt(h)}–${hourAt(h + 1)}`;
const timesText = (f) => (Array.isArray(f) ? `${Math.min(...f)}–${Math.max(...f)} times`
  : Number(f) === 2 ? 'twice' : `${f} times`);

/** What a band lost (L), its neighbours gained (G) and G/L, as the bake gives them. */
const lgText = (vi) => `L ${num(vi.corridor_vehicles_lost)} · G ${vi.parallel_vehicles_gained === null ? '—' : num(vi.parallel_vehicles_gained)}`
  + ` · G/L ${ratioText(vi.gained_over_lost)}`;

/** The neighborhood bands over the window: per band, vehicles on each group's streets in
 *  route_cost.order's columns (no charge, the scenario the route-cost one is compared
 *  against, it); what the band streets lost (L) and the streets beside them gained (G).
 *  The rule's inputs, and a verdict only where the bake writes one. */
export function CorridorPanel({ workshop, period }) {
  const id = routeCostId(workshop);
  if (!id || !period) return null;
  const m = workshop.model.scenarios[id];
  const rc = corridorsIn(workshop, period);
  const byId = Object.fromEntries(workshop.scenarios.map((s) => [s.id, s]));
  const cols = m.routeCost.order.map((k) => byId[k] || { id: k, code: '?', name: k, color: '#777' });
  const names = rc ? Object.keys(rc.corridors) : [];
  const reported = names.some((n) => Object.keys(rc.corridors[n].reported || {}).length);
  const vi = rc && rc.verdict_inputs;
  return (
    <div className="ws-panel ws-corridors">
      <div className="ws-panel-title">Neighborhood bands{rc && <ModelTag />}</div>
      <div className="ws-panel-sub">
        Scenario {cols[2].code}&apos;s drivers count the residential streets of {names.length || 'its'} bands
        at {timesText(m.routeCost.factor)} their travel time. Vehicles on them and beside them, {period.label}.
      </div>
      {!rc && <div className="ws-reason-cell">{reasonFor(workshop, id, period) || 'No band figures for these hours.'}</div>}
      {rc && names.map((name) => {
        const c = rc.corridors[name];
        const street = m.routeCost.corridors[name]?.main_street;
        return (
          <div className="ws-corridor" key={name}>
            <div className="ws-corridor-name">{corridorName(name)}{street && <span className="ws-corridor-street">{street}</span>}</div>
            <table className="ws-table ws-corridor-table">
              <thead>
                <tr>
                  <th scope="col">Vehicles</th>
                  {cols.map((s) => (
                    <th scope="col" key={s.id} title={s.name}><span className="ws-code" style={{ background: s.color }}>{s.code}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupsOf(c).map((g) => (
                  <tr key={g.key}>
                    <th scope="row">{groupName(g, m.routeCost.factor)}</th>
                    {cols.map((s) => <td key={s.id}>{int(g.value.vehicles[s.id])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ws-corridor-lg">{lgText(c.verdict_inputs)}</div>
            {c.verdict_inputs.note && <div className="ws-corridor-note">{noteText(c.verdict_inputs.note, hourRange)}</div>}
          </div>
        );
      })}
      {rc && (
        <>
          <div className="ws-corridor-all">
            {names.length === 2 ? 'Both bands' : 'All bands'}, each vehicle once: {lgText(vi)}.
            {' '}Against scenario {cols[1].code}: entries into the zone {signedInt(vi.entries_change)}, vehicle miles {signedInt(vi.total_vmt_change_mi)}.
          </div>
          {vi.note && <div className="ws-corridor-note">{noteText(vi.note, hourRange)}</div>}
          {rc.verdict && <div className="ws-corridor-lg">Verdict: {noteText(rc.verdict, hourRange)}</div>}
          <div className="ws-muted">
            Each vehicle counted once in an hour. L is what the band streets lost against scenario {cols[1].code},
            G what the same-class residential streets beside them gained; the method reads G ≥ 0.5 L as the
            burden moving onto those streets.
            {reported && ' The other groups are reported only: traffic moving onto them is the aim.'}
          </div>
        </>
      )}
    </div>
  );
}
